import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Form, Input, Button, Card, Typography, Alert, Space } from 'antd';
import { LockOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { invitationService } from '@municipality/firebase';
import type { InvitationData } from '@municipality/firebase';

const { Title, Text } = Typography;

const getSuperAdminRoleLabel = (role?: string): string => {
  if (!role) return 'Super admin';
  const labels: Record<string, string> = {
    super_admin: 'Super admin',
    administrator: 'Administrator',
    region_manager: 'Region manager',
    content_manager: 'Content manager',
    support: 'Support',
  };
  return labels[role] || role;
};

const generateCSRFToken = (): string => {
  const array = new Uint8Array(32);
  if (typeof window !== 'undefined' && window.crypto) {
    window.crypto.getRandomValues(array);
  }
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

interface ActivationFormData {
  password: string;
  confirmPassword: string;
  csrfToken: string;
}

export const Activate: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [csrfToken, setCsrfToken] = useState<string>('');

  const token = searchParams.get('token');

  const loadInvitation = useCallback(async () => {
    if (!token) return;
    try {
      const invitationData = await invitationService.getInvitationByToken(token);
      if (!invitationData) {
        setError('Invalid or expired activation token');
        setLoading(false);
        return;
      }
      if (invitationData.role !== 'super_admin') {
        setError('This activation link is for the super admin portal. Please use the correct link from your invitation email.');
        setLoading(false);
        return;
      }
      setInvitation(invitationData);
    } catch (err) {
      console.error('Error loading invitation:', err);
      setError('Failed to load invitation');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    setCsrfToken(generateCSRFToken());
    if (!token) {
      setError('No activation token provided');
      setLoading(false);
      return;
    }
    loadInvitation();
  }, [token, loadInvitation]);

  const handleSubmit = async (values: ActivationFormData) => {
    if (!token || !invitation) return;
    if (values.csrfToken !== csrfToken) {
      setError('Security validation failed. Please refresh the page and try again.');
      return;
    }
    setSubmitting(true);
    try {
      await invitationService.acceptInvitation(token, values.password);
      setSuccess(true);
      globalThis.setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      console.error('Error activating account:', err);
      setError((err as Error)?.message || 'Failed to activate account');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Card style={{ width: 400, padding: 24, textAlign: 'center' }}>
          <Title level={3}>Loading...</Title>
          <Text type="secondary">Loading activation form...</Text>
        </Card>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Card style={{ width: 400, padding: 24 }}>
          <Title level={3}>Activation error</Title>
          <Alert type="error" message={error} style={{ marginBottom: 16 }} />
          <Button type="primary" onClick={() => navigate('/login')} block>
            Go to login
          </Button>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Card style={{ width: 400, padding: 24, textAlign: 'center' }}>
          <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
          <Title level={3}>Account activated</Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Your account has been successfully activated. Redirecting to login...
          </Text>
          <Button type="primary" onClick={() => navigate('/login')} block>
            Login now
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <Card style={{ width: 450, padding: 24 }}>
        <Title level={3} style={{ textAlign: 'center' }}>Activate your account</Title>
        <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: 24 }}>
          Set up your account to get started
        </Text>

        <div style={{ marginBottom: 24 }}>
          <Text strong>Invitation details</Text>
        </div>
        <Space direction="vertical" style={{ width: '100%', marginBottom: 24 }}>
          <div>
            <Text type="secondary">Role: </Text>
            <Text strong>{getSuperAdminRoleLabel(invitation?.superAdminRole)}</Text>
          </div>
          <div>
            <Text type="secondary">Email: </Text>
            <Text strong>{invitation?.email}</Text>
          </div>
          {(invitation?.name || invitation?.surname) && (
            <div>
              <Text type="secondary">Name: </Text>
              <Text strong>{[invitation.name, invitation.surname].filter(Boolean).join(' ')}</Text>
            </div>
          )}
        </Space>

        {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ password: '', confirmPassword: '', csrfToken }}
        >
          <Form.Item
            label="Password"
            name="password"
            rules={[
              { required: true, message: 'Please enter a password' },
              { min: 8, message: 'Password must be at least 8 characters' },
              {
                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                message: 'Password must contain uppercase, lowercase, number and special character (@$!%*?&)',
              },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Enter your password" size="large" />
          </Form.Item>
          <Form.Item
            label="Confirm password"
            name="confirmPassword"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Please confirm your password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) return Promise.resolve();
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Confirm your password" size="large" />
          </Form.Item>
          <Form.Item name="csrfToken" hidden>
            <Input />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={submitting} block size="large">
              Activate account
            </Button>
          </Form.Item>
        </Form>

        <div style={{ padding: 16, backgroundColor: '#f6f8fa', borderRadius: 6, marginTop: 16 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Your password will be securely stored and encrypted. Choose a strong password you can remember.
          </Text>
        </div>
      </Card>
    </div>
  );
};
